import type { RouteRecordRaw } from 'vue-router'
import { AppModule } from '@/config/types'
import { ticketIdViewParam, ticketsTableId } from '@/services/domain/tickets/const'
import { ticketsRouteNames } from '@/routes/user/tickets/types'
import TicketStatus from '@/data/models/domain/tickets/TicketStatus'
import Ticket from '@/data/models/domain/tickets/Ticket'
import type { FilteredViewTableApplyResolverParams } from '@/services/domain/filteredViews/resolvers/types'
import { FILTERED_VIEW_PARAM_NAME } from '@/services/domain/filteredViews/const'
import type { DomainTitlerParams } from '@/services/route/titlers/types'
import FilteredView from '@/data/models/domain/filteredViews/FilteredView'
import type { TicketByWorkspaceIdResolverParams } from '@/services/domain/tickets/resolvers/ticketByWorkspaceIdResolverService'
import type { ComponentProps } from '@/composition/types'
import type TicketFilteredViewDetails from '@/components/views/tickets/TicketFilteredViewDetails.vue'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'

export const ticketsGettingStartedRoute: RouteRecordRaw = {
  name: ticketsRouteNames.gettingStarted,
  path: 'getting-started',
  meta: {
    modules: [AppModule.ComingSoon],
    resolverComponent: () => import('@/components/domain/tickets/resolvers/TicketCommonResolver.vue'),
  },
  component: () => import('@/components/views/tickets/TicketsGettingStarted.vue'),
}

export const ticketsFilterRoute: RouteRecordRaw = {
  name: ticketsRouteNames.tableFilteredView,
  path: `filter/:${FILTERED_VIEW_PARAM_NAME}`,
  meta: {
    resolvers: [
      {
        service: 'ModelResolver',
        params: [TicketStatus],
      },
      {
        service: 'FilteredViewTableApplyResolverService',
        params: {
          tableModelId: ticketsTableId,
          routeParamName: FILTERED_VIEW_PARAM_NAME,
          redirectToIfNotFound: { name: ticketsRouteNames.root },
          keepSortingAndColumnsForSystem: true,
          shouldBuildTableId: true,
        } satisfies FilteredViewTableApplyResolverParams,
      },
      {
        service: 'TicketServerEventsResolverService',
      },
    ],
    resolverComponent: () => import('@/components/domain/tickets/resolvers/TicketCommonResolver.vue'),
    titler: {
      service: 'DomainTitlerService',
      params: {
        model: FilteredView,
        field: 'name',
        idParamName: FILTERED_VIEW_PARAM_NAME,
      } as DomainTitlerParams,
    },
  },
  props: (route) => {
    const tagQueryKey: keyof NonNullable<TmRoutesDictionary[typeof ticketsRouteNames.root]['query']> = 'tags'
    return {
      filteredViewId: route.params[FILTERED_VIEW_PARAM_NAME],
      tags: route.query[tagQueryKey],
    } satisfies ComponentProps<typeof TicketFilteredViewDetails>
  },
  component: () => import('@/components/views/tickets/TicketFilteredViewDetails.vue'),
}

export const ticketsDetailsRoute: RouteRecordRaw = {
  name: ticketsRouteNames.ticketDetails,
  path: `:${ticketIdViewParam}`,
  component: () => import('@/components/views/tickets/TicketDetails.vue'),
  meta: {
    modules: [
      AppModule.Typing,
      AppModule.Note,
      AppModule.AlsoHere,
      AppModule.WhatsApp,
      AppModule.Facebook,
      AppModule.Instagram,
      AppModule.WebWidgets,
      AppModule.Contacts,
      AppModule.ContactsBlocked,
      AppModule.ContactsUnsubscribed,
    ],
    resolvers: [
      {
        service: 'ModelResolver',
        params: [TicketStatus],
      },
      {
        service: 'TicketServerEventsResolverService',
      },
      {
        service: 'TicketDetailsServerEventsResolverService',
      },
      {
        service: 'TypingResolver',
      },
      [
        {
          service: 'TicketByWorkspaceIdResolverService',
          params: {
            redirectToIfNotFound: { name: ticketsRouteNames.root },
          } satisfies TicketByWorkspaceIdResolverParams,
        },
        {
          service: 'TicketMarkAsReadResolverService',
        },
      ],
    ],
    resolverComponent: () => import('@/components/domain/tickets/resolvers/TicketCommonResolver.vue'),
    titler: {
      service: 'DomainTitlerService',
      params: {
        model: Ticket,
        field: 'subject',
        idParamName: ticketIdViewParam,
      } as DomainTitlerParams,
    },
  },
}

export const ticketsRootRoute: RouteRecordRaw = {
  name: ticketsRouteNames.root,
  path: 'tickets',
  component: () => import('@/components/views/tickets/Tickets.vue'),
  meta: {
    tags: ['USER_TOP_MENU'],
    tabTitle: 'pageTitle.user.tickets.tickets',
    modules: [AppModule.FilteredViews, AppModule.Tickets, AppModule.Tags, AppModule.AlsoHere],
  },
  redirect: { name: ticketsRouteNames.resolveNavigation },
  children: [
    {
      name: ticketsRouteNames.resolveNavigation,
      path: '',
      meta: {
        resolvers: [
          [
            {
              service: 'TicketsBasicDataResolverService',
            },
            {
              service: 'TicketDefaultRouteRedirectResolverService',
            },
            {
              service: 'FetchAllAccountsResolver',
            },
            {
              service: 'TicketFirstLastMessageResolverService',
            },
            {
              service: 'AlsoHereResolver',
            },
          ],
        ],
        resolverComponent: () => import('@/components/domain/tickets/resolvers/TicketCommonResolver.vue'),
      },
      children: [ticketsGettingStartedRoute, ticketsFilterRoute, ticketsDetailsRoute],
    },
  ],
}
